/**
 * 아티스트 관련 Write 작업 목차
 * 
 * - createArtist() - 아티스트 생성
 * - updateArtist() - 아티스트 수정
 * - deleteArtist() - 아티스트 삭제
 * - setArtistApproval() - 승인 상태 변경
 * - setArtistImage() - 아티스트 이미지 설정
 * - removeArtistImage() - 아티스트 이미지 삭제
 */

import apiClient from "@/utils/api";
import { trackActivity, ACTIVITY_TYPES } from "@/utils/activityTracker";

/**
 * 아티스트 생성
 * @param {string} artistName - 아티스트 이름
 * @param {Object} artistData - 아티스트 데이터
 * @returns {Promise<void>}
 */
export async function createArtist(artistName, artistData) {
  try {
    await apiClient.post("/api/artist", {
      name: artistName,
      ...artistData,
    });

    // 아티스트 등록 활동 기록
    trackActivity(ACTIVITY_TYPES.ARTIST_CREATE, {
      target: "artist",
      targetId: artistName,
    });
  } catch (error) {
    console.error("아티스트 생성 오류:", error);
    throw error;
  }
}

/**
 * 아티스트 수정
 * @param {string} artistId - 아티스트 ID
 * @param {Object} updateData - 수정할 데이터
 * @returns {Promise<void>}
 */
export async function updateArtist(artistId, updateData) {
  try {
    await apiClient.put(`/api/artist/${encodeURIComponent(artistId)}`, updateData);

    // 아티스트 수정 활동 기록
    trackActivity(ACTIVITY_TYPES.ARTIST_UPDATE, {
      target: "artist",
      targetId: artistId,
    });
  } catch (error) {
    console.error("아티스트 수정 오류:", error);
    throw error;
  }
}

/**
 * 아티스트 삭제
 * @param {string} artistId - 아티스트 ID
 * @returns {Promise<void>}
 */
export async function deleteArtist(artistId) {
  try {
    await apiClient.delete(`/api/artist/${encodeURIComponent(artistId)}`);
  } catch (error) {
    console.error("아티스트 삭제 오류:", error);
    throw error;
  }
}

/**
 * 아티스트 승인 상태 변경
 * @param {string} artistName - 아티스트 이름
 * @param {boolean} approved - 승인 여부
 * @returns {Promise<void>}
 */
export async function setArtistApproval(artistName, approved) {
  try {
    await apiClient.patch(`/api/artist/${encodeURIComponent(artistName)}/approval`, {
      approved,
    });
  } catch (error) {
    console.error("아티스트 승인 상태 변경 오류:", error);
    throw error;
  }
}

/**
 * 아티스트 이미지 설정
 * @param {string} artistName - 아티스트 이름
 * @param {File} imageFile - 이미지 파일
 * @returns {Promise<void>}
 */
export async function setArtistImage(artistName, imageFile) {
  try {
    if (!imageFile) {
      throw new Error("이미지 파일이 제공되지 않았습니다.");
    }

    if (!(imageFile instanceof File) && !(imageFile instanceof Blob)) {
      console.error("전달된 imageFile:", imageFile);
      throw new Error("유효한 파일 객체가 아닙니다.");
    }

    const formData = new FormData();
    formData.append("image", imageFile);

    // axios가 FormData를 감지하여 자동으로 boundary를 포함한 Content-Type 설정
    const response = await apiClient.post(`/api/artist/${encodeURIComponent(artistName)}/image`, formData);
    return response.data;
  } catch (error) {
    console.error("아티스트 이미지 설정 오류:", error);
    throw error;
  }
}

/**
 * 아티스트 이미지 삭제
 * @param {string} artistName - 아티스트 이름
 * @returns {Promise<void>}
 */
export async function removeArtistImage(artistName) {
  try {
    await apiClient.delete(`/api/artist/${encodeURIComponent(artistName)}/image`);
  } catch (error) {
    console.error("아티스트 이미지 삭제 오류:", error);
    throw error;
  }
}

/**
 * 아티스트 이름 변경 (관련 컬렉션 모두 업데이트)
 * - lives 컬렉션의 lineup 배열
 * - videos 컬렉션의 lineup 배열
 * - artists 컬렉션의 members 배열 (밴드 멤버)
 * @param {string} oldName - 기존 아티스트 이름
 * @param {string} newName - 새 아티스트 이름
 * @returns {Promise<void>}
 */
export async function renameArtist(oldName, newName) {
  try {
    await apiClient.post("/api/artist/rename", {
      oldName,
      newName,
    });

    // 아티스트 이름 변경 활동 기록
    trackActivity(ACTIVITY_TYPES.ARTIST_UPDATE, {
      target: "artist",
      targetId: newName,
      metadata: {
        action: "rename",
        oldName,
        newName,
      },
    });
  } catch (error) {
    console.error("아티스트 이름 변경 오류:", error);
    throw error;
  }
}
